import { useEffect, useState } from 'react'
import {
  Layout,
  Typography,
  Input,
  Select,
  Button,
  Pagination,
  Empty,
  message,
  Spin,
  Space
} from 'antd'
import { Search, ArrowRight } from 'lucide-react'
import { ProductCard, ProductLibrarySchedule } from './components/ProductCard'
import { Navigation } from '../../components/Navigation/Navigation'
import { useApi } from '../../services/ApiContext'
import { ProductLibraryItem } from '../../types/ffe'
import { Link } from 'react-router-dom'

const { Content } = Layout
const { Title, Text } = Typography

const sortOptions = [
  {
    value: 'timesUsed',
    label: 'Most Used',
    field: 'timesUsed',
    order: -1
  },
  {
    value: 'createdAt',
    label: 'Recently Added',
    field: 'createdAt',
    order: -1
  },
  { value: 'name_asc', label: 'Name (A-Z)', field: 'name', order: 1 },
  { value: 'name_desc', label: 'Name (Z-A)', field: 'name', order: -1 }
]

const PAGE_SIZE = 12

const EmptyLibrary = () => (
  <div className="text-center py-16 px-4">
    <div className="max-w-[800px] mx-auto">
      <img
        src="/save-to-product-library.png"
        alt="Product Library"
        className="rounded-lg mx-auto mb-8 shadow-lg"
      />
      <div className="text-center max-w-lg mx-auto">
        <Title level={3}>Build Your Product Library</Title>
        <Text className="text-gray-600 block mb-8">
          Save time on future projects by building a reusable library of your
          favourite furniture, fixtures, and finishes. Add items directly from
          your FF&E schedules to make them available for quick reuse.
        </Text>
        <Space direction="vertical" className="w-full" size="large">
          <Link to="/app/documents">
            <Button
              type="primary"
              size="large"
              iconPosition="end"
              icon={<ArrowRight className="h-4 w-4" />}
              block
            >
              Open FF&E Schedule
            </Button>
          </Link>
        </Space>
      </div>
    </div>
  </div>
)

export const ProductLibraryPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('createdAt')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [products, setProducts] = useState<Array<ProductLibraryItem>>([])
  const [totalItems, setTotalItems] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const api = useApi()
  const [schedules, setSchedules] = useState<Array<ProductLibrarySchedule>>([])
  const [selectedScheduleId, setSelectedScheduleId] = useState<
    string | undefined | null
  >(null)
  const [selectedGroupId, setSelectedGroupId] = useState<
    string | undefined | null
  >(null)
  const [loadingAddToSchedule, setLoadingAddToSchedule] = useState(false)

  const handleAddToSchedule = async (
    productId: string,
    scheduleId?: string,
    groupId?: string
  ) => {
    if (!scheduleId) {
      message.error('Please select a project first')
      return
    }
    setLoadingAddToSchedule(true)
    await api.addProductFromLibraryToSchedule(productId, scheduleId, groupId)
    setLoadingAddToSchedule(false)
    message.success('Product added to schedule')
  }

  const handleDelete = async (productId: string) => {
    setLoading(true)
    await api.removeProductLibraryItem(productId)
    message.success('Product deleted from library')
    await searchProductLibrary()
    setLoading(false)
  }

  const loadSchedules = async () => {
    const response = await api.findDocuments({
      $limit: 5,
      $sort: { updatedAt: -1 },
      $select: ['projectTitle', '_id', 'groups', 'updatedAt']
    })

    setSchedules(response.data)
  }

  useEffect(() => {
    loadSchedules()
  }, [])

  const searchProductLibrary = async () => {
    const sortOption = sortOptions.find((option) => option.value === sortBy)

    setLoading(true)
    const response = await api.findProductLibraryItem({
      $search: searchTerm,
      $limit: PAGE_SIZE,
      $skip: (currentPage - 1) * PAGE_SIZE,
      $sort: sortOption ? { [sortOption.field]: sortOption.order } : undefined
    })
    setLoading(false)

    setProducts(response.data)
    setTotalItems(response.total)
  }

  useEffect(() => {
    searchProductLibrary()
  }, [searchTerm, sortBy, currentPage])

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, sortBy])

  return (
    <Layout className="min-h-screen">
      <Navigation />
      <Content className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <Title level={4}>Product Library</Title>
            {/* <Button
              type="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => message.info('Add product form would open here')}
            >
              Add Product
            </Button> */}
          </div>

          <div className="flex gap-4 mb-6">
            <Input
              prefix={<Search className="h-4 w-4 text-gray-400" />}
              placeholder="Search products..."
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />

            <Select
              value={sortBy}
              onChange={setSortBy}
              options={sortOptions}
              style={{ width: 200 }}
            />
          </div>

          <Spin spinning={loading}>
            {products.length > 0 || loading ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                  {products.map((product) => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      schedules={schedules}
                      selectedScheduleId={selectedScheduleId}
                      setSelectedScheduleId={setSelectedScheduleId}
                      onAddToSchedule={handleAddToSchedule}
                      selectedGroupId={selectedGroupId}
                      setSelectedGroupId={setSelectedGroupId}
                      onDelete={handleDelete}
                      loadingAddToSchedule={loadingAddToSchedule}
                    />
                  ))}
                </div>

                <div className="flex justify-center">
                  <Pagination
                    current={currentPage}
                    total={totalItems}
                    pageSize={PAGE_SIZE}
                    onChange={setCurrentPage}
                    showSizeChanger={false}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`
                    }
                  />
                </div>
              </>
            ) : (
              <>
                {searchTerm ? (
                  <Empty
                    description={
                      <span>No products found matching your search</span>
                    }
                  />
                ) : (
                  <EmptyLibrary />
                )}
              </>
            )}
          </Spin>
        </div>
      </Content>
    </Layout>
  )
}
